import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiCheck, FiMinus, FiPlus, FiShoppingBag, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

function CartPage() {
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    addToCart,
    appliedDiscountCode,
    applyDiscountCode,
  } = useContext(CartContext);
  const [discountInput, setDiscountInput] = useState(appliedDiscountCode);
  const [discountMessage, setDiscountMessage] = useState(
    appliedDiscountCode
      ? `${appliedDiscountCode} applied — ${siteConfig.commerce.discountRate * 100}% off`
      : ""
  );

  const detailedItems = useMemo(() => cartItems.map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) })).filter((item) => item.product), [cartItems]);
  const subtotal = detailedItems.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const discountApplied = appliedDiscountCode === siteConfig.commerce.discountCode;
  const discount = discountApplied ? subtotal * siteConfig.commerce.discountRate : 0;
  const discountedSubtotal = subtotal - discount;
  const shipping = siteConfig.commerce.standardShipping;
  const total = discountedSubtotal + shipping;
  const recommendations = productData.filter((product) => !cartItems.some((item) => item.id === product.id)).slice(17, 21);

  const applyDiscount = (event) => {
    event.preventDefault();
    if (applyDiscountCode(discountInput)) {
      setDiscountMessage(`${siteConfig.commerce.discountCode} applied — ${siteConfig.commerce.discountRate * 100}% off`);
    } else {
      setDiscountMessage(`That code is not recognised. Try ${siteConfig.commerce.discountCode}.`);
    }
  };

  const renderRecommendations = () => (
    <section className="cart-recommendations page-shell">
      <div><p className="eyebrow">You may also like</p><h2>Made for the same journey</h2></div>
      <div className="cart-recommendations__grid">
        {recommendations.map((product) => (
          <article key={product.id}>
            <Link to={`/shopDetails/${product.id}`}><img src={product.image} alt={`${product.name} — ${product.colorName}`} loading="lazy" /></Link>
            <div><Link to={`/shopDetails/${product.id}`}><strong>{product.name}</strong><small>{product.colorName}</small></Link><span>{formatPrice(product.price)}</span></div>
            <div className="cart-recommendations__sizes">{product.sizes.map((size) => <button key={size} type="button" onClick={() => addToCart(product.id, 1, size, product.colors[0])}>{size}</button>)}</div>
          </article>
        ))}
      </div>
    </section>
  );

  if (detailedItems.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <section className="cart-empty">
          <FiShoppingBag />
          <p className="eyebrow">Your shopping bag</p>
          <h1>It appears your bag is empty.</h1>
          <p>Explore useful layers, breathable fibres and versatile colour for everyday wardrobes.</p>
          <Link className="button-primary" to="/shop">Continue shopping</Link>
        </section>
        {renderRecommendations()}
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-header page-shell">
        <p className="eyebrow">Your selection</p>
        <h1>Shopping bag <span>({detailedItems.reduce((totalCount, item) => totalCount + item.quantity, 0)})</span></h1>
      </header>

      <div className="cart-shipping-progress page-shell">
        <p><FiCheck /> Fixed standard shipping is <strong>{formatPrice(shipping)}</strong> for every checkout.</p>
        <div><span style={{ width: "100%" }} /></div>
      </div>

      <div className="cart-layout page-shell">
        <section className="cart-items" aria-label="Cart items">
          {detailedItems.map((item) => (
            <article className="cart-item" key={`${item.id}-${item.size}-${item.color}`}>
              <Link className="cart-item__image" to={`/shopDetails/${item.product.id}`}><img src={item.product.image} alt={`${item.product.name} — ${item.product.colorName}`} /></Link>
              <div className="cart-item__body">
                <div className="cart-item__top">
                  <Link to={`/shopDetails/${item.product.id}`}><h2>{item.product.name}</h2><p>{item.product.colorName}</p></Link>
                  <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                </div>
                <dl><div><dt>Colour</dt><dd>{item.color}</dd></div><div><dt>Size</dt><dd>{item.size}</dd></div><div><dt>Status</dt><dd>{item.product.status}</dd></div></dl>
                <div className="cart-item__bottom">
                  <div className="cart-quantity" aria-label={`Quantity for ${item.product.name}`}>
                    <button type="button" onClick={() => item.quantity === 1 ? removeFromCart(item.id, item.size, item.color) : updateQuantity(item.id, item.size, item.color, item.quantity - 1)} aria-label="Decrease quantity"><FiMinus /></button>
                    <span>{item.quantity}</span>
                    <button type="button" onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)} aria-label="Increase quantity"><FiPlus /></button>
                  </div>
                  <button className="cart-remove" type="button" onClick={() => removeFromCart(item.id, item.size, item.color)}><FiTrash2 /> Remove</button>
                </div>
              </div>
            </article>
          ))}
          <Link className="cart-continue" to="/shop"><FiArrowLeft /> Continue shopping</Link>
        </section>

        <aside className="cart-summary">
          <h2>Order summary</h2>
          <div className="cart-summary__rows"><p><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></p>{discountApplied && <p className="cart-summary__discount"><span>{appliedDiscountCode}</span><strong>−{formatPrice(discount)}</strong></p>}<p><span>Shipping</span><strong>{shipping === 0 ? "Free" : formatPrice(shipping)}</strong></p></div>
          <form className="cart-discount" onSubmit={applyDiscount}><label htmlFor="discount-code">Discount code</label><div><input id="discount-code" value={discountInput} onChange={(event) => setDiscountInput(event.target.value)} placeholder="Enter code" /><button type="submit">Apply</button></div>{discountMessage && <p className={discountApplied ? "is-success" : ""}>{discountMessage}</p>}</form>
          <div className="cart-total"><span>Total <small>Destination duties may apply</small></span><strong>{formatPrice(total)} <small>{siteConfig.business.currency}</small></strong></div>
          <Link className="button-primary" to="/checkout">Checkout securely</Link>
          <p className="cart-summary__note">Payment continues on Stripe.</p>
          <details><summary>Delivery & returns <span>+</span></summary><p>Order-specific delivery updates are sent by email. For a return, contact customer care promptly after delivery and follow the published condition and process rules.</p></details>
        </aside>
      </div>
      {renderRecommendations()}
    </main>
  );
}

export default CartPage;
